import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { projectsApi, catalogApi, HistoryEntry } from "../api";
import { useCanEdit, useIsAdmin } from "../auth";
import { ConfigEditor } from "../components/editor";
import { TokenEditor } from "../components/TokenEditor";
import { TemplateCustomizationEditor } from "../components/project/TemplateCustomizationEditor";
import { DataSourcesTab } from "../components/project/DataSourcesTab";
import { UpdateConfigModal } from "../components/UpdateConfigModal";
import { DeployConfigModal } from "../components/DeployConfigModal";
import {
  ArrowLeft,
  Trash2,
  FolderKanban,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  History,
  RotateCcw,
  Loader2,
  AlertTriangle,
  FileJson,
  Copy,
  Check,
  Upload,
  ArrowUpCircle,
  Package,
} from "lucide-react";
import clsx from "clsx";
import { usePublicSettings } from "../hooks/usePublicSettings";

const STATUS_CONFIG = {
  draft: { label: "Bozza", color: "bg-gray-100 text-gray-700", icon: Clock },
  valid: {
    label: "Validato",
    color: "bg-blue-100 text-blue-700",
    icon: CheckCircle,
  },
  exported: {
    label: "Esportato",
    color: "bg-green-100 text-green-700",
    icon: Download,
  },
};

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canEdit = useCanEdit();
  const isAdmin = useIsAdmin();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showUpdateConfig, setShowUpdateConfig] = useState(false);
  const [showDeployConfig, setShowDeployConfig] = useState(false);
  const [editingDeployedUrl, setEditingDeployedUrl] = useState(false);
  const [deployedUrlValue, setDeployedUrlValue] = useState("");
  const { get: getSetting } = usePublicSettings();
  const historyPageSize = getSetting<number>("projects.historyPageSize");

  // Carica progetto
  const {
    data: projectData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["project", id],
    queryFn: () => projectsApi.get(id!),
    enabled: Boolean(id),
  });

  // Carica history
  const { data: historyData } = useQuery({
    queryKey: ["project-history", id, historyPageSize],
    queryFn: () => projectsApi.getHistory(id!, { limit: historyPageSize }),
    enabled: Boolean(id) && showHistory,
  });

  // Mutations
  const validateMutation = useMutation({
    mutationFn: () => projectsApi.validate(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project", id] });
    },
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      // 1. Esporta (aggiorna stato)
      const response = await projectsApi.export(id!);

      // 2. Scarica il file
      const blob = new Blob(
        [JSON.stringify(response.data.data.config, null, 2)],
        { type: "application/json" },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${project?.name || "config"}-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project", id] });
      queryClient.invalidateQueries({ queryKey: ["project-history", id] });
    },
  });

  const packageMutation = useMutation({
    mutationFn: async (deployConfig?: Record<string, unknown>) => {
      const response = await projectsApi.generatePackage(id!, deployConfig);
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const code = client?.code || "site";
      const name = project!.name.toLowerCase().replace(/\s+/g, "-");
      a.download = `onecreator-${code}-${name}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return response;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => projectsApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      navigate("/projects");
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (version: number) => projectsApi.restoreVersion(id!, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project", id] });
      queryClient.invalidateQueries({ queryKey: ["project-history", id] });
      setShowHistory(false);
    },
  });

  const updateDeployedUrlMutation = useMutation({
    mutationFn: (deployedUrl: string) =>
      projectsApi.update(id!, { deployedUrl }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project", id] });
      setEditingDeployedUrl(false);
    },
  });

  // Query per versioni catalogo disponibili (solo per admin/editor)
  const { data: catalogVersionsData } = useQuery({
    queryKey: ["catalog-versions"],
    queryFn: () =>
      catalogApi.getVersions({
        status: "published",
        limit: getSetting<number>("catalog.versionsPageSize"),
      }),
    enabled: canEdit,
  });

  // Mutation per upgrade versione catalogo
  const upgradeCatalogMutation = useMutation({
    mutationFn: (newVersion: number) =>
      projectsApi.update(id!, { productCatalogVersion: newVersion }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project", id] });
      queryClient.invalidateQueries({ queryKey: ["project-tokens", id] });
    },
  });

  const project = projectData?.data.data.project;
  const history = historyData?.data.data.history || [];
  const client =
    project?.clientId && typeof project.clientId === "object"
      ? project.clientId
      : null;
  const catalogVersions = catalogVersionsData?.data.data || [];
  const latestCatalogVersion =
    catalogVersions.length > 0
      ? Math.max(...catalogVersions.map((c: any) => c.version))
      : null;

  const handleCopyConfig = async () => {
    if (!project) return;
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(project.config, null, 2),
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleDownloadConfig = () => {
    if (!project) return;
    const blob = new Blob([JSON.stringify(project.config, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${client?.code || "project"}-${project.name.toLowerCase().replace(/\s+/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto text-red-400 mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Progetto non trovato
        </h2>
        <p className="text-gray-600 mb-4">
          Il progetto richiesto non esiste o è stato eliminato.
        </p>
        <Link to="/projects" className="btn-primary inline-flex">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna alla lista
        </Link>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/projects")}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Torna ai progetti
        </button>

        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center">
              <FolderKanban className="w-7 h-7 text-primary-600" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  {project.name}
                </h1>
                <span
                  className={clsx(
                    "flex items-center text-xs px-2 py-0.5 rounded-full",
                    statusConfig.color,
                  )}
                >
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusConfig.label}
                </span>
              </div>
              {client && (
                <Link
                  to={`/clients/${client.id}`}
                  className="text-primary-600 hover:underline"
                >
                  {client.name}
                </Link>
              )}
            </div>
          </div>

          {canEdit && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="btn-secondary"
              >
                <History className="w-4 h-4 mr-2" />
                Storico
              </button>
              {isAdmin && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="btn-danger"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* History panel */}
      {showHistory && (
        <div className="card mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Cronologia versioni</h2>
          </div>
          {history.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              Nessuna versione salvata
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 max-h-64 overflow-auto">
              {history.map((entry: HistoryEntry) => (
                <li
                  key={entry.id}
                  className="px-6 py-3 flex items-center justify-between"
                >
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">
                        v{entry.version}
                      </span>
                      <span
                        className={clsx(
                          "text-xs px-1.5 py-0.5 rounded",
                          entry.action === "created"
                            ? "bg-green-100 text-green-700"
                            : entry.action === "exported"
                              ? "bg-blue-100 text-blue-700"
                              : entry.action === "restored"
                                ? "bg-amber-100 text-amber-700"
                                : "bg-gray-100 text-gray-600",
                        )}
                      >
                        {entry.action}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {new Date(entry.savedAt).toLocaleString("it-IT")}
                      {entry.savedBy && ` • ${entry.savedBy.name}`}
                    </p>
                    {entry.changeDescription && (
                      <p className="text-xs text-gray-600 mt-1">
                        {entry.changeDescription}
                      </p>
                    )}
                  </div>
                  {canEdit && (
                    <button
                      onClick={() => restoreMutation.mutate(entry.version)}
                      disabled={restoreMutation.isPending}
                      className="text-xs text-primary-600 hover:text-primary-700 flex items-center"
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      Ripristina
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Validazione */}
          {project.validation && (
            <div
              className={clsx(
                "card p-6",
                project.validation.valid
                  ? "bg-green-50 border-green-200"
                  : "bg-red-50 border-red-200",
              )}
            >
              <div className="flex items-start space-x-3">
                {project.validation.valid ? (
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <h3
                    className={clsx(
                      "font-medium",
                      project.validation.valid
                        ? "text-green-800"
                        : "text-red-800",
                    )}
                  >
                    {project.validation.valid
                      ? "Configurazione valida"
                      : "Errori di validazione"}
                  </h3>
                  {!project.validation.valid &&
                    project.validation.errors.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {project.validation.errors.map((err, i) => (
                          <li key={i} className="text-sm text-red-700">
                            <code className="text-xs bg-red-100 px-1 rounded">
                              {err.path}
                            </code>
                            : {err.message}
                          </li>
                        ))}
                      </ul>
                    )}
                  <p className="text-xs text-gray-500 mt-2">
                    Ultima verifica:{" "}
                    {new Date(project.validation.checkedAt).toLocaleString(
                      "it-IT",
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Config preview */}
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Configurazione</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleCopyConfig}
                  className="text-xs text-gray-600 hover:text-gray-900 flex items-center"
                >
                  {copied ? (
                    <Check className="w-3 h-3 mr-1" />
                  ) : (
                    <Copy className="w-3 h-3 mr-1" />
                  )}
                  {copied ? "Copiato!" : "Copia"}
                </button>
                <button
                  onClick={handleDownloadConfig}
                  className="text-xs text-gray-600 hover:text-gray-900 flex items-center"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Scarica
                </button>
                {canEdit && (
                  <button
                    onClick={() => setShowUpdateConfig(true)}
                    className="text-xs text-primary-600 hover:text-primary-800 flex items-center font-medium"
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    Importa
                  </button>
                )}
              </div>
            </div>
            <div className="p-4 bg-gray-900 rounded-b-lg overflow-auto max-h-96">
              <pre className="text-xs text-gray-300 font-mono">
                {JSON.stringify(project.config, null, 2)}
              </pre>
            </div>
          </div>

          {/* Config Editor */}
          <ConfigEditor project={project} canEdit={canEdit} />

          {/* Design Tokens Editor */}
          <TokenEditor projectId={project.id} canEdit={canEdit} />

          {/* Template Customization Editor */}
          <TemplateCustomizationEditor
            projectId={project.id}
            catalogVersion={project.productCatalogVersion}
            readOnly={!canEdit}
          />

          {/* Data Sources */}
          {canEdit && (
            <div className="card">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">Data Sources</h2>
              </div>
              <div className="p-6">
                <DataSourcesTab projectId={project.id} />
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <div className="card p-6 space-y-3">
            <h2 className="font-semibold text-gray-900 mb-4">Azioni</h2>

            <button
              onClick={() => validateMutation.mutate()}
              disabled={validateMutation.isPending}
              className="btn-secondary w-full justify-center"
            >
              {validateMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Valida configurazione
            </button>

            <button
              onClick={() => exportMutation.mutate()}
              disabled={exportMutation.isPending}
              className="btn-primary w-full justify-center"
            >
              {exportMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileJson className="w-4 h-4 mr-2" />
              )}
              Esporta JSON
            </button>

            <button
              onClick={() => setShowDeployConfig(true)}
              disabled={packageMutation.isPending}
              className="btn-primary w-full justify-center"
            >
              {packageMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Package className="w-4 h-4 mr-2" />
              )}
              Genera Pacchetto Deploy
            </button>

            {packageMutation.isError && (
              <p className="text-sm text-red-600">
                {(packageMutation.error as any)?.response?.data?.error
                  ?.message ||
                  "Errore nella generazione del pacchetto. Verificare che i site artifacts siano buildati."}
              </p>
            )}

            {project.deployedUrl && (
              <a
                href={`${project.deployedUrl}/deploy`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary w-full justify-center text-center"
              >
                <Upload className="w-4 h-4 mr-2" />
                Carica Configurazione
              </a>
            )}
          </div>

          {/* Metadata */}
          <div className="card p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Informazioni</h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Cliente</dt>
                <dd>
                  {client ? (
                    <Link
                      to={`/clients/${client.id}`}
                      className="text-primary-600 hover:underline"
                    >
                      {client.name}
                    </Link>
                  ) : (
                    <span className="text-gray-900">-</span>
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Stato</dt>
                <dd
                  className={clsx(
                    project.status === "exported"
                      ? "text-green-600"
                      : project.status === "valid"
                        ? "text-blue-600"
                        : "text-gray-600",
                  )}
                >
                  {statusConfig.label}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Catalog v.</dt>
                <dd className="flex items-center gap-1.5">
                  <span className="text-gray-900">
                    {project.productCatalogVersion}
                  </span>
                  {latestCatalogVersion &&
                    project.productCatalogVersion < latestCatalogVersion && (
                      <>
                        <span
                          className="text-amber-500"
                          title={`Versione ${latestCatalogVersion} disponibile`}
                        >
                          <AlertTriangle className="w-3.5 h-3.5" />
                        </span>
                        {canEdit && (
                          <button
                            onClick={() =>
                              upgradeCatalogMutation.mutate(
                                latestCatalogVersion,
                              )
                            }
                            disabled={upgradeCatalogMutation.isPending}
                            className="ml-1 text-xs text-primary-600 hover:text-primary-800 flex items-center"
                            title={`Aggiorna a v${latestCatalogVersion}`}
                          >
                            {upgradeCatalogMutation.isPending ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                <ArrowUpCircle className="w-3.5 h-3.5 mr-0.5" />
                                v{latestCatalogVersion}
                              </>
                            )}
                          </button>
                        )}
                      </>
                    )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Creato il</dt>
                <dd className="text-gray-900">
                  {new Date(project.createdAt).toLocaleDateString("it-IT")}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Modificato il</dt>
                <dd className="text-gray-900">
                  {new Date(project.updatedAt).toLocaleDateString("it-IT")}
                </dd>
              </div>
              {project.lastExportedAt && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Ultimo export</dt>
                  <dd className="text-gray-900">
                    {new Date(project.lastExportedAt).toLocaleDateString(
                      "it-IT",
                    )}
                  </dd>
                </div>
              )}
            </dl>

            {/* Deployed URL Editor */}
            {canEdit && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Sito Deployed
                </label>
                {editingDeployedUrl ? (
                  <div className="space-y-2">
                    <input
                      type="url"
                      value={deployedUrlValue}
                      onChange={(e) => setDeployedUrlValue(e.target.value)}
                      placeholder="https://example.com"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          updateDeployedUrlMutation.mutate(deployedUrlValue);
                        }}
                        disabled={updateDeployedUrlMutation.isPending}
                        className="btn-primary text-xs px-3 py-1.5"
                      >
                        {updateDeployedUrlMutation.isPending ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Check className="w-3 h-3" />
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setEditingDeployedUrl(false);
                          setDeployedUrlValue(project.deployedUrl || "");
                        }}
                        className="btn-secondary text-xs px-3 py-1.5"
                      >
                        Annulla
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {project.deployedUrl ? (
                      <>
                        <a
                          href={project.deployedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary-600 hover:underline truncate flex-1"
                        >
                          {project.deployedUrl}
                        </a>
                        <button
                          onClick={() => {
                            setDeployedUrlValue(project.deployedUrl || "");
                            setEditingDeployedUrl(true);
                          }}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Modifica
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          setDeployedUrlValue("");
                          setEditingDeployedUrl(true);
                        }}
                        className="text-sm text-gray-500 hover:text-primary-600"
                      >
                        + Aggiungi URL
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Conferma eliminazione
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              Sei sicuro di voler eliminare il progetto{" "}
              <strong>{project.name}</strong>?
              <span className="block mt-2 text-red-600">
                Questa azione è irreversibile. Anche lo storico delle versioni
                verrà eliminato.
              </span>
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-secondary"
              >
                Annulla
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="btn-danger"
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminazione...
                  </>
                ) : (
                  "Conferma eliminazione"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Config Modal */}
      <UpdateConfigModal
        isOpen={showUpdateConfig}
        onClose={() => setShowUpdateConfig(false)}
        projectId={project.id}
        projectName={project.name}
        currentConfig={project.config}
      />

      {/* Deploy Config Modal */}
      <DeployConfigModal
        isOpen={showDeployConfig}
        onClose={() => setShowDeployConfig(false)}
        onConfirm={(deployConfig) =>
          packageMutation.mutate(
            deployConfig as Record<string, unknown> | undefined,
          )
        }
        projectName={project.name}
      />
    </div>
  );
}
